/**
 * ActivityFeed — flux d'activité audit_log filtré + redacté (S6 PR5).
 *
 * Liste chronologique inversée des dernières actions sur les entités
 * exposables (works, drafts, sales, mockups, queue_items, ...). Le
 * service `dashboard_service.get_recent_activity` applique :
 *   - allowlist stricte (jamais users/platform_connections)
 *   - redaction tokens dans le payload
 *
 * Affichage : libellé FR par couple `(entity_type, action)`. Couvert
 * par `_describe(entry)` qui dispatch.
 */

import { useDashboardActivity, type ActivityEntry } from '@/hooks/useDashboard'

import './ActivityFeed.css'

const DEFAULT_LIMIT = 30

export default function ActivityFeed() {
  const query = useDashboardActivity(DEFAULT_LIMIT)

  if (query.isLoading) {
    return (
      <section className="activity-feed" aria-busy="true">
        <h2 className="ui-h4 activity-feed-title">Activité récente</h2>
        <p className="ui-body ui-text-muted">Chargement…</p>
      </section>
    )
  }

  if (query.isError) {
    return (
      <section className="activity-feed">
        <h2 className="ui-h4 activity-feed-title">Activité récente</h2>
        <p className="ui-body ui-text-muted">Indisponible.</p>
      </section>
    )
  }

  const entries = query.data ?? []

  return (
    <section className="activity-feed" aria-label="Activité récente">
      <h2 className="ui-h4 activity-feed-title">Activité récente</h2>
      {entries.length === 0 ? (
        <p className="ui-body ui-text-muted">Aucune activité récente.</p>
      ) : (
        <ol className="activity-feed-list">
          {entries.map((entry) => (
            <ActivityRow key={entry.id} entry={entry} />
          ))}
        </ol>
      )}
    </section>
  )
}

interface ActivityRowProps {
  entry: ActivityEntry
}

function ActivityRow({ entry }: ActivityRowProps) {
  const description = _describe(entry)
  const date = new Date(entry.created_at).toLocaleString('fr-FR', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
  return (
    <li className="activity-feed-row">
      <span className="ui-body-sm ui-text-muted activity-feed-time">{date}</span>
      <span className="ui-body activity-feed-text">{description}</span>
    </li>
  )
}

/**
 * Dispatch (entity_type, action) → libellé FR. Pour les couples non
 * mappés explicitement, fallback `${action} sur ${entity_type}#${id}`
 * (lisible mais générique). Les nouvelles actions S7+ peuvent enrichir
 * ce dispatch sans casser l'existant (fallback graceful).
 */
function _describe(entry: ActivityEntry): string {
  const id = entry.entity_id !== null ? `#${entry.entity_id}` : ''
  const key = `${entry.entity_type}.${entry.action}`
  switch (key) {
    case 'works.create':
      return `Œuvre ${id} créée`
    case 'works.update':
      return `Œuvre ${id} modifiée`
    case 'works.archive':
      return `Œuvre ${id} archivée`
    case 'drafts.create':
      return `Draft ${id} généré`
    case 'drafts.approve':
      return `Draft ${id} approuvé`
    case 'drafts.reject':
      return `Draft ${id} rejeté`
    case 'platform_resources.publish':
      return `Publication ${id} mise en ligne`
    case 'platform_resources.state_ended':
      return `Publication ${id} retirée`
    case 'platform_resources.state_deleted':
      return `Publication ${id} supprimée définitivement`
    case 'platform_resources.state_active':
      return `Publication ${id} renouvelée`
    case 'sales.ingest':
      return `Vente ${id} reçue`
    case 'mockups.render':
      return `Mockup ${id} composé`
    case 'mockups.supersede':
      return `Mockup ${id} remplacé par une nouvelle version`
    case 'queue_items.enqueue_publish_draft':
      return `Job publication mis en file`
    case 'queue_items.enqueue_update_publication':
      return `Job mise à jour publication mis en file`
    case 'queue_items.enqueue_end_publication':
      return `Job retrait publication mis en file`
    case 'queue_items.enqueue_renew_publication':
      return `Job renouvellement publication mis en file`
    case 'queue_items.enqueue_delete_publication':
      return `Job suppression définitive mis en file`
    default:
      return `${entry.action} sur ${entry.entity_type}${id}`
  }
}
